import type {
  TaviReportRow,
  TaviReportSection,
} from '@components/ReportTable/TaviReportTable';
import styles from './TaviReportModalContent.module.scss';
import { useMemo, useState } from 'react';
import Radio from '@components/Radio';

interface Props {
  imageSrc?: {
    images?: { one?: string };
  };
  value?: TaviReportSection[];
}

type ValveRange = {
  size: string;
  range: [number, number];
};

type ValveOption = {
  size: string;
  model: string;
  metricLabel: string;
  ranges: ValveRange[];
};

type ValveResult = {
  sizes: string[];
  isOverlap: boolean;
};

const SAPIEN3 = {
  diameter: [
    { size: '20mm', range: [16, 19] },
    { size: '23mm', range: [18, 22] },
    { size: '26mm', range: [21, 25] },
    { size: '29mm', range: [24, 28] },
  ] as ValveRange[],
  area: [
    { size: '20mm', range: [273, 345] },
    { size: '23mm', range: [338, 430] },
    { size: '26mm', range: [430, 546] },
    { size: '29mm', range: [540, 683] },
  ] as ValveRange[],
};

const EVOLUT_R: ValveRange[] = [
  { size: '23mm', range: [56.5, 62.8] },
  { size: '26mm', range: [62.8, 72.3] },
  { size: '29mm', range: [72.3, 81.7] },
  { size: '34mm', range: [81.7, 94.2] },
];

const ACURATE_NEO: ValveRange[] = [
  { size: '23mm', range: [66, 72] },
  { size: '25mm', range: [72, 79] },
  { size: '27mm', range: [79, 85] },
  { size: '29mm', range: [85, 91] },
];

const valveOptions: ValveOption[] = [
  {
    size: '20mm',
    model: 'SAPIENS 3 (Diameter)',
    metricLabel: '(Long Axis + Short Axis) / 2',
    ranges: SAPIEN3.diameter,
  },
  {
    size: '23mm',
    model: 'SAPIENS 3 (Area)',
    metricLabel: 'Area',
    ranges: SAPIEN3.area,
  },
  {
    size: '26mm',
    model: 'Evolut R (Perimeter)',
    metricLabel: 'Perimeter',
    ranges: EVOLUT_R,
  },
  {
    size: '29mm',
    model: 'ACURATE neo (Perimeter)',
    metricLabel: 'Perimeter',
    ranges: ACURATE_NEO,
  },
];

const formatValue = (row: TaviReportRow): string => {
  const { value, decimals = 1, suffix = '' } = row;

  if (value === null || value === undefined || value === '') {
    return '-';
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    return `${value.toFixed(decimals)}${suffix}`;
  }

  return `${String(value)}${suffix}`;
};

const getAnnulusValueByLabel = (
  sections: TaviReportSection[] | undefined,
  label: string,
): number | null => {
  const annulusSection = sections?.find((section) => section.title === 'Annulus');
  const rowValue = annulusSection?.rows.find((row) => row.label === label)?.value;

  if (typeof rowValue === 'number' && Number.isFinite(rowValue)) {
    return Math.round(rowValue * 10) / 10;
  }

  return null;
};

const getValveResult = (value: number, ranges: ValveRange[]): ValveResult => {
  const sizes = ranges
    .filter(({ range: [min, max] }) => value >= min && value <= max)
    .map(({ size }) => size);

  return {
    sizes,
    isOverlap: sizes.length > 1,
  };
};

const getStripeClassName = (index: number): string =>
  index % 2 === 1 ? styles.rowEven : styles.rowOdd;

export default function TaviReportModalContent({ imageSrc, value }: Props) {
  const [selectedModelIndex, setSelectedModelIndex] = useState<number>(0);
  const selectedValveOption = valveOptions[selectedModelIndex] ?? valveOptions[0];

  const valveSizeOptions = useMemo(
    () => selectedValveOption.ranges.map(({ size }) => size),
    [selectedValveOption],
  );

  const selectedValveResult = useMemo(() => {
    const annulusValue = getAnnulusValueByLabel(value, selectedValveOption.metricLabel);

    if (annulusValue === null) {
      return null;
    }

    return getValveResult(annulusValue, selectedValveOption.ranges);
  }, [selectedValveOption, value]);

  const matchedSizes = useMemo(
    () => new Set(selectedValveResult?.sizes ?? []),
    [selectedValveResult],
  );

  const isOverlap = selectedValveResult?.isOverlap ?? false;

  const renderMetricSection = (section: TaviReportSection) => (
    <>
      <div className={styles.sectionTitleRow}>
        <span>{section.title}</span>
      </div>
      {section.rows.map((row, index) => (
        <div
          className={`${styles.metricRow} ${getStripeClassName(index)}`}
          key={`${section.title}-${row.label}`}
        >
          <span className={styles.metricLabelCell}>{row.label}</span>
          <span className={styles.metricValueCell}>{formatValue(row)}</span>
        </div>
      ))}
    </>
  );

  return (
    <div className={styles.mainContainer}>
      <div className={styles.contentContainer}>
        <div className={styles.imageContainer}>
          <img src={imageSrc?.images?.one} />
        </div>

        <div className={styles.tableContainer}>
          <div className={styles.table}>
            <div className={styles.metricSection}>
              <div className={styles.metricHeaderRow}>
                <div className={styles.measurementHeaderCell}>
                  <span>Measurement</span>
                </div>
                <div className={styles.valueHeaderCell}>
                  <span>Value</span>
                </div>
              </div>
              {(value ?? []).map((section) => renderMetricSection(section))}
            </div>
          </div>

          <div className={styles.table}>
            <div className={styles.valveSection}>
              <div className={styles.sectionHeaderRow}>
                <span>Valve Sizing</span>
              </div>

              {valveOptions.map((option, index) => {
                const valveSize = valveSizeOptions[index] ?? option.size;
                const isMatchedSize = matchedSizes.has(valveSize);
                const matchClassName = isMatchedSize
                  ? isOverlap
                    ? styles.sizeOverlap
                    : styles.sizeMatch
                  : '';

                return (
                  <div
                    className={`${styles.valveRow} ${getStripeClassName(index)}`}
                    key={`${option.size}-${option.model}`}
                  >
                    <span
                      className={`${styles.valveSizeCell} ${matchClassName}`}
                    >
                      {valveSize}
                    </span>

                    <div className={styles.valveOptionCell}>
                      <div className={styles.valveOption}>
                        <span className={styles.valveOptionLabel}>
                          <Radio
                            value={String(index)}
                            checked={selectedModelIndex === index}
                            onChange={() => {
                              setSelectedModelIndex(index);
                            }}
                          />
                          {option.model}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
